// Contexte de l'application : client de l'API, identité, carte affichée, état de l'API.
import { useQuery, useQueryClient } from '@tanstack/react-query';
import { createContext, useCallback, useContext } from 'react';
import type { ClientEkera } from './api/client';
import type { Moi, Sante, VueAnssi } from './api/types';
import type { FournisseurIdentite } from './auth/identite';

export interface Contexte {
  client: ClientEkera;
  identite: FournisseurIdentite;
  cid: string;
  /** L'appelant tel que l'API le voit (GET /v0/moi) : identité vérifiée et rôles déduits du jeton. */
  moi: Moi;
  /** État de l'API (GET /v0/sante) : mode d'authentification (« oidc-http » : démonstration déclarée). */
  sante: Sante;
  /** Vrai si l'API accepte des écritures ET si le jeton porte le rôle « ecriture » : sinon, aucun bouton d'écriture. */
  ecritureAutorisee: boolean;
  /** Rôle « administration » : journal d'accès. */
  administrateur: boolean;
  /** Rôle « partage » (O18), distinct de l'écriture : ouvrir ce qui sort de l'entreprise (raisons, signaux). */
  peutPartager: boolean;
  /** Pourquoi l'écriture n'est pas proposée (texte affiché), vide si elle l'est. */
  motifLectureSeule: string;
  /** Démonstration déclarée par /config.json (W3) : seule condition de la mention « données fictives ». */
  demonstration: boolean;
}

export const ContexteEkera = createContext<Contexte | null>(null);

export function useEkera(): Contexte {
  const c = useContext(ContexteEkera);
  if (!c) throw new Error('ContexteEkera absent');
  return c;
}

export function useAuteur(): string {
  const { moi, identite } = useEkera();
  return identite.utilisateur()?.nom ?? moi.nom ?? 'inconnu';
}

// ---------------------------------------------------------------- lectures (TanStack Query)

export function useCarte() {
  const { client, cid } = useEkera();
  return useQuery({ queryKey: ['carte', cid], queryFn: () => client.carte(cid) });
}
export function useCalcul() {
  const { client, cid } = useEkera();
  return useQuery({ queryKey: ['carte', cid, 'scenarios'], queryFn: () => client.scenarios(cid) });
}
export function useConstats() {
  const { client, cid } = useEkera();
  return useQuery({ queryKey: ['carte', cid, 'constats'], queryFn: () => client.constats(cid) });
}
export function useContradictions() {
  const { client, cid } = useEkera();
  return useQuery({ queryKey: ['carte', cid, 'contradictions'], queryFn: () => client.contradictions(cid) });
}
export function usePieces() {
  const { client, cid } = useEkera();
  return useQuery({ queryKey: ['carte', cid, 'pieces'], queryFn: () => client.pieces(cid) });
}
export function useVariantes() {
  const { client, cid } = useEkera();
  return useQuery({ queryKey: ['carte', cid, 'variantes'], queryFn: () => client.variantes(cid) });
}
export function usePreuves(element: string | null) {
  const { client, cid } = useEkera();
  return useQuery({
    queryKey: ['carte', cid, 'preuves', element],
    queryFn: () => client.preuves(cid, element ?? undefined),
    enabled: !!element,
  });
}
export function useJournal() {
  const { client, cid } = useEkera();
  return useQuery({ queryKey: ['carte', cid, 'journal'], queryFn: () => client.journal(cid) });
}
export function useEvenements() {
  const { client, cid } = useEkera();
  return useQuery({ queryKey: ['carte', cid, 'evenements'], queryFn: () => client.evenements(cid) });
}
export function useVueAnssi(vue: VueAnssi | null) {
  const { client, cid } = useEkera();
  return useQuery({
    queryKey: ['carte', cid, 'anssi', vue],
    queryFn: () => client.vueAnssi(cid, vue as VueAnssi),
    enabled: !!vue,
  });
}
export function useJournalAcces(actif: boolean) {
  const { client } = useEkera();
  return useQuery({ queryKey: ['acces'], queryFn: () => client.acces(200), enabled: actif });
}
export function useObjetsRetires() {
  const { client, cid } = useEkera();
  return useQuery({
    queryKey: ['carte', cid, 'objets-retires'],
    queryFn: async () => (await client.objets(cid, { inclureRetires: true })).filter((o) => o.retire),
  });
}

/** Après une écriture acceptée : tout ce qui dépend de la carte est relu. */
export function useRelire(): () => Promise<void> {
  const qc = useQueryClient();
  const { cid } = useEkera();
  return useCallback(() => qc.invalidateQueries({ queryKey: ['carte', cid] }), [qc, cid]);
}
